/**
 * Authentication Store
 *
 * Manages user authentication state using Zustand with persistence.
 * Provides login, logout, and registration functionality with JWT token handling.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials, RegisterData } from '../types/auth';
import api from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  clearError: () => void;
}

/**
 * Auth Store with Zustand
 *
 * Uses persist middleware to save auth state to localStorage for session persistence.
 * Token is stored and automatically included in API requests via the API client.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/auth/login', { user: credentials });

          // Extract JWT token from Authorization header
          const authHeader = response.headers.authorization || response.headers.Authorization;
          const token = authHeader?.replace('Bearer ', '') || response.data.token || '';

          set({
            user: response.data.user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'An error occurred during login',
          });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/auth/register', { user: data });

          // Extract JWT token from Authorization header
          const authHeader = response.headers.authorization || response.headers.Authorization;
          const token = authHeader?.replace('Bearer ', '') || response.data.token || '';

          set({
            user: response.data.user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'An error occurred during registration',
          });
          throw error;
        }
      },

      logout: () => {
        // Clear auth state and remove from localStorage
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        // Only persist user and token, not loading/error states
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
